import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Calendar, LogOut, Sun, Moon } from 'lucide-react';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import { useLogoutMutation } from '../../api/authApi';
import ConfirmDialog from '../ConfirmDialog';
import logo from '../../assets/logo.png';

export default function DoctorShell() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const refreshToken = useSelector((s) => s.auth?.refreshToken);
  const [doLogout] = useLogoutMutation();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('healing.theme') || 'light');

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('healing.theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };

  const handleLogout = async () => {
    setConfirmLogout(false);
    if (refreshToken) {
      try { await doLogout({ refreshToken }).unwrap(); } catch { /* best-effort */ }
    }
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const initials = (user?.fullName || user?.userName || 'D')
    .split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="doctor-shell">
      <header className="doctor-header">
        <div className="doctor-header-brand">
          <img src={logo} alt="The Healing Clinic" height={32} style={{ objectFit: 'contain' }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
              The Healing Clinic
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={10} /> Calendar
            </div>
          </div>
        </div>

        <div className="doctor-header-right">
          <button className="btn-icon" aria-label="Toggle theme" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="avatar av-sm">{initials}</div>
            <div className="doctor-user-info">
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                {user?.fullName || user?.userName || '—'}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Doctor</div>
            </div>
          </div>

          <button
            className="btn btn-sm btn-outline"
            onClick={() => setConfirmLogout(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="doctor-body">
        <Outlet />
      </main>

      <ConfirmDialog
        open={confirmLogout}
        title="Sign out?"
        message="You will need to log in again to access the calendar."
        confirmLabel="Sign out"
        variant="danger"
        onConfirm={handleLogout}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
}
